// Client-side functions for secure API calls to our Render backend
// This replaces the old Supabase integration

import { toast } from "react-hot-toast";
import {
  getApiBaseUrl,
  handleApiResponse,
  getAuthToken,
  makeApiCall,
} from "../utils/api.js";

// Authentication functions
export const signUp = async (email, password, fullName) => {
  try {
    const data = await makeApiCall("/api/auth/signup", {
      method: "POST",
      body: JSON.stringify({ email, password, fullName }),
    });
    return { data, error: null };
  } catch (error) {
    console.error("Signup error:", error);
    return { data: null, error };
  }
};

export const signIn = async (email, password) => {
  try {
    const data = await makeApiCall("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    return { data, error: null };
  } catch (error) {
    console.error("Login error:", error);
    return { data: null, error };
  }
};

export const signOut = async () => {
  try {
    // Clear local storage
    localStorage.removeItem("auth_token");
    localStorage.removeItem("user_data");
    return { error: null };
  } catch (error) {
    console.error("Logout error:", error);
    return { error };
  }
};

export const resetPassword = async (email) => {
  try {
    const data = await makeApiCall("/api/auth/reset-password", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
    return { data, error: null };
  } catch (error) {
    console.error("Reset password error:", error);
    return { data: null, error };
  }
};

export const getCurrentUser = async () => {
  try {
    const token = getAuthToken();
    if (!token) {
      return { data: null, error: new Error("No auth token found") };
    }

    const data = await makeApiCall("/api/auth/me");
    return { data: data.user, error: null };
  } catch (error) {
    console.error("Get current user error:", error);
    return { data: null, error };
  }
};

export const getCurrentSession = async () => {
  try {
    const token = getAuthToken();
    if (!token) {
      return { data: null, error: new Error("No session found") };
    }

    const userData = localStorage.getItem("user_data");
    if (userData) {
      return {
        data: {
          session: { access_token: token },
          user: JSON.parse(userData),
        },
        error: null,
      };
    }

    return { data: null, error: new Error("No session data found") };
  } catch (error) {
    console.error("Get current session error:", error);
    return { data: null, error };
  }
};

// User profile functions
export const createUserProfile = async (userId, profileData) => {
  try {
    // Profile creation is handled automatically in signup
    return { data: { success: true }, error: null };
  } catch (error) {
    console.error("Create profile error:", error);
    return { data: null, error };
  }
};

export const getUserProfile = async (userId) => {
  try {
    const data = await makeApiCall("/api/users/profile");
    return { data, error: null };
  } catch (error) {
    console.error("Get user profile error:", error);
    return { data: null, error };
  }
};

export const updateUserProfile = async (userId, updates) => {
  try {
    const data = await makeApiCall("/api/users/profile", {
      method: "PUT",
      body: JSON.stringify(updates),
    });
    return { data: data.user, error: null };
  } catch (error) {
    console.error("Update profile error:", error);
    return { data: null, error };
  }
};

// Conversation functions
export const getUserConversations = async () => {
  try {
    const data = await makeApiCall("/api/conversations");
    return { data, error: null };
  } catch (error) {
    console.error("Get conversations error:", error);
    return { data: null, error };
  }
};

export const createConversation = async (title, aiPersonality, modelUsed) => {
  try {
    const data = await makeApiCall("/api/conversations", {
      method: "POST",
      body: JSON.stringify({ title, aiPersonality, modelUsed }),
    });
    return { data, error: null };
  } catch (error) {
    console.error("Create conversation error:", error);
    return { data: null, error };
  }
};

export const updateConversationTitle = async (conversationId, title) => {
  try {
    const data = await makeApiCall(`/api/conversations/${conversationId}`, {
      method: "PUT",
      body: JSON.stringify({ title }),
    });
    return { data, error: null };
  } catch (error) {
    console.error("Update conversation title error:", error);
    return { data: null, error };
  }
};

export const deleteConversation = async (conversationId) => {
  try {
    const data = await makeApiCall(`/api/conversations/${conversationId}`, {
      method: "DELETE",
    });
    return { data, error: null };
  } catch (error) {
    console.error("Delete conversation error:", error);
    return { data: null, error };
  }
};

// Message functions
export const getMessages = async (conversationId) => {
  try {
    const data = await makeApiCall(`/api/messages/${conversationId}`);
    return { data, error: null };
  } catch (error) {
    console.error("Get messages error:", error);
    return { data: null, error };
  }
};

export const saveMessage = async (
  conversationId,
  content,
  role,
  metadata = {}
) => {
  try {
    const data = await makeApiCall("/api/messages", {
      method: "POST",
      body: JSON.stringify({
        conversationId,
        content,
        role,
        metadata,
        tokenCount: Math.ceil(content.length / 4), // Simple token estimation
      }),
    });
    return { data, error: null };
  } catch (error) {
    console.error("Save message error:", error);
    return { data: null, error };
  }
};

export const searchUserMessages = async (searchQuery, limit = 50) => {
  try {
    const data = await makeApiCall("/api/messages/search", {
      method: "POST",
      body: JSON.stringify({ query: searchQuery, limit }),
    });
    return { data, error: null };
  } catch (error) {
    console.error("Search messages error:", error);
    return { data: null, error };
  }
};
